<template>
  <DefaultLayout>
    <div class="payment-history-page">
      <div class="page-header">
        <h1>Historial de Pagos</h1>
        <p class="subtitle">Consulta tus pagos y transacciones</p>
      </div>

      <!-- Current Plan Info -->
      <Card v-if="currentTenant" class="current-plan-card mb-4">
        <template #content>
          <div class="plan-info-grid">
            <div class="info-item">
              <i class="pi pi-credit-card info-icon"></i>
              <div>
                <span class="info-label">Plan Actual</span>
                <span class="info-value">{{ currentTenant.plan_name || 'Sin plan' }}</span>
              </div>
            </div>
            
            <div class="info-item">
              <i class="pi pi-circle-fill info-icon" :style="{ color: getStatusColor(currentTenant.subscription_status) }"></i>
              <div>
                <span class="info-label">Estado</span>
                <span class="info-value">
                  <Tag 
                    :value="getStatusLabel(currentTenant.subscription_status)" 
                    :severity="getStatusSeverity(currentTenant.subscription_status)"
                  />
                </span>
              </div>
            </div>
            
            <div class="info-item" v-if="currentTenant.subscription_status === 'active' && nextPaymentDate">
              <i class="pi pi-calendar info-icon"></i>
              <div>
                <span class="info-label">Próximo Pago</span>
                <span class="info-value">{{ nextPaymentDate }}</span>
              </div>
            </div>

            <div class="info-item">
              <i class="pi pi-bolt info-icon"></i>
              <div>
                <span class="info-label">Tokens</span>
                <span class="info-value">
                  {{ (currentTenant.tokens_used || 0).toLocaleString() }} / 
                  {{ (currentTenant.included_tokens || 0).toLocaleString() }}
                </span>
              </div>
            </div>
          </div>
        </template>
      </Card>

      <!-- Payment History Table -->
      <Card class="payment-history-card">
        <template #title>
          <div class="card-header">
            <span>Historial de Transacciones</span>
            <Button 
              label="Actualizar" 
              icon="pi pi-refresh" 
              size="small"
              text
              @click="fetchPaymentOrders"
              :loading="loading"
            />
          </div>
        </template>
        <template #content>
          <div v-if="loading" class="loading-state">
            <ProgressSpinner style="width: 50px; height: 50px" />
            <p>Cargando historial de pagos...</p>
          </div>
          <TenantPaymentHistory 
            v-else
            :orders="paymentOrders" 
            :isAdmin="false"
            @link-regenerated="fetchPaymentOrders"
          />
        </template>
      </Card>
    </div>
  </DefaultLayout>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useToast } from 'primevue/usetoast';
import { useAuthStore } from '../stores/auth';
import DefaultLayout from '../components/layout/DefaultLayout.vue';
import TenantPaymentHistory from '../components/payments/TenantPaymentHistory.vue';
import Card from 'primevue/card';
import Tag from 'primevue/tag';
import Button from 'primevue/button';
import ProgressSpinner from 'primevue/progressspinner';
import api from '../services/api';

const toast = useToast();
const authStore = useAuthStore();

const currentTenant = ref(null);
const paymentOrders = ref([]);
const loading = ref(false);

// Fetch current tenant information
const fetchCurrentTenant = async () => {
  try {
    if (!authStore.user?.tenant_id) return;
    const response = await api.get(`/tenants/${authStore.user.tenant_id}`);
    currentTenant.value = response.data;
  } catch (error) {
    console.error('Error fetching tenant:', error);
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Error al cargar información del tenant',
      life: 5000
    });
  }
};

// Fetch payment orders for the current tenant
const fetchPaymentOrders = async () => {
  loading.value = true;
  try {
    const response = await api.get('/payments/my-orders');
    paymentOrders.value = response.data;
  } catch (error) {
    console.error('Error fetching payment orders:', error);
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Error al cargar el historial de pagos',
      life: 5000
    });
  } finally {
    loading.value = false;
  }
};

// Calculate next payment date (30 days from last reset)
const nextPaymentDate = computed(() => {
  if (!currentTenant.value?.last_reset_date) return null;
  
  const lastReset = new Date(currentTenant.value.last_reset_date);
  const nextPayment = new Date(lastReset);
  nextPayment.setDate(nextPayment.getDate() + 30);
  
  return nextPayment.toLocaleDateString('es-CO', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
});

// Get status label
const getStatusLabel = (status) => {
  const labels = {
    active: 'Activo',
    inactive: 'Inactivo',
    past_due: 'Vencido',
    suspended: 'Suspendido',
    cancelled: 'Cancelado',
    pending_payment: 'Pago Pendiente'
  };
  return labels[status] || status;
};

// Get status severity for PrimeVue Tag
const getStatusSeverity = (status) => {
  const severities = {
    active: 'success',
    inactive: 'secondary',
    past_due: 'warning',
    suspended: 'danger',
    cancelled: 'danger',
    pending_payment: 'warning'
  };
  return severities[status] || 'info';
};

// Get status color for icon
const getStatusColor = (status) => {
  const colors = {
    active: '#10b981',
    inactive: '#6b7280',
    past_due: '#f59e0b',
    suspended: '#ef4444',
    cancelled: '#ef4444',
    pending_payment: '#f59e0b'
  };
  return colors[status] || '#6b7280';
};

onMounted(async () => {
  await Promise.all([
    fetchCurrentTenant(),
    fetchPaymentOrders()
  ]);
});
</script>

<style scoped>
.payment-history-page {
  max-width: 1200px;
  margin: 0 auto;
  padding: 1rem;
}

.page-header {
  margin-bottom: 1.5rem;
}

.page-header h1 {
  margin-bottom: 0.25rem;
  color: #1e293b;
  font-size: 2rem;
}

.subtitle {
  color: #6b7280;
  font-size: 1rem;
}

.current-plan-card {
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border: 1px solid #bae6fd;
}

.plan-info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.info-icon {
  font-size: 1.5rem;
  color: #3b82f6;
}

.info-label {
  display: block;
  font-size: 0.875rem;
  color: #6b7280;
  margin-bottom: 0.25rem;
}

.info-value {
  display: block;
  font-size: 1rem;
  font-weight: 600;
  color: #1e293b;
}

.mb-4 {
  margin-bottom: 1.5rem;
}

.payment-history-card {
  margin-bottom: 2rem;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  color: #6b7280;
}

.loading-state p {
  margin-top: 1rem;
  font-size: 0.95rem;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .plan-info-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
  
  .page-header h1 {
    font-size: 1.5rem;
  }
}
</style>
